import { SeededTeam, TeamScore, TourneyData } from "../types/Tournament";
import * as config from "../config";

const baseUrl = "https://europe-west1-mysper-e5462.cloudfunctions.net";

/**
 * Punkte der Teams innerhalb eines Zeitfensters [startTime, endTime).
 * Liefert eine schlanke TeamScore-Liste.
 *
 * lockTime (optional): Für abgeschlossene Runden zählen nur Einträge mit
 * loggedAt < lockTime, damit spätere Nachträge das Ergebnis nicht kippen.
 * Liegt lockTime in der Zukunft, hat es keinen Effekt (Runde läuft live).
 *
 * teamIds (optional): Schränkt die Auswertung auf genau diese Teams ein. Das
 * Backend liefert dann für JEDES angeforderte Team einen Eintrag (auch ohne
 * Aktivität -> score 0). Wird für die Live-Vorschau der laufenden Runde genutzt
 * (Punkte der gesetzten Teams im aktiven Fenster).
 */
export async function getTeamScoresInWindow(
  startTime: number,
  endTime: number,
  lockTime?: number,
  teamIds?: string[],
): Promise<TeamScore[]> {
  let url =
    baseUrl +
    "/getTeamScoresInWindow?competitionId=" +
    config.COMPETITION_ID +
    `&maxTeams=${config.BRACKET_SIZE}` +
    `&startTime=${startTime}` +
    `&endTime=${endTime}`;
  if (lockTime != null) {
    url += `&lockTime=${lockTime}`;
  }
  if (teamIds && teamIds.length > 0) {
    url += `&teamIds=${encodeURIComponent(teamIds.join(","))}`;
  }

  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      redirect: "follow",
    });
    const json = await response.json();
    return (json.teams || []) as TeamScore[];
  } catch (e) {
    console.log(e);
    return [];
  }
}

/**
 * Single Source of Truth des Turniers aus der DB. Ein Endpoint liefert:
 * - config: Timing (tournamentStart, aktive/Pause-Tage, lockHours, totalRounds)
 * - seeds:  die N gesetzten Teams, keyed nach Setzplatz "1".."N" (inkl. Name +
 *           country), feste Zuordnung – NICHT nach Score sortieren
 * - rounds: nur bereits gelockte Runden, je { teams: { teamId -> finale Punkte } }
 *
 * Namen kommen ausschließlich aus seeds (rounds enthält nur teamId -> Punkte).
 * Fehlt eine Runde, ist sie noch nicht gelockt -> die Microsite rechnet sie live.
 */
export async function getTourneyResults(): Promise<TourneyData> {
  const url =
    baseUrl + "/getTourneyResults?competitionId=" + config.COMPETITION_ID;
  const seeds = new Map<number, SeededTeam>();
  const rounds = new Map<number, Map<string, number>>();
  let tourneyConfig: TourneyData["config"];

  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      redirect: "follow",
    });
    const json = await response.json();

    if (json?.config) {
      const c = json.config;
      tourneyConfig = {
        tournamentStart: Number(c.tournamentStart),
        roundActiveDays: Number(c.roundActiveDays),
        roundPauseDays: Number(c.roundPauseDays),
        roundLockHours: Number(c.roundLockHours),
        totalRounds: Number(c.totalRounds),
      };
    }

    const seedsObj = json?.seeds || {};
    for (const key of Object.keys(seedsObj)) {
      const s = seedsObj[key] || {};
      const seedNo = Number(key);
      seeds.set(seedNo, {
        id: s.teamId,
        name: s.teamName ?? "",
        seed: seedNo,
        divisionName: s.country ? { default: String(s.country) } : undefined,
      });
    }

    const roundsObj = json?.rounds || {};
    for (const key of Object.keys(roundsObj)) {
      const teams = roundsObj[key]?.teams || {};
      const scores = new Map<string, number>();
      for (const id of Object.keys(teams)) {
        scores.set(id, Number(teams[id]) || 0);
      }
      rounds.set(Number(key), scores);
    }
  } catch (e) {
    console.log(e);
  }

  return { config: tourneyConfig, seeds, rounds };
}
